import React, { Children, cloneElement, Component, ReactElement } from 'react';

import { SelectableIteratorAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType, ContainerProvider, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, isFunction, isMobile, propagateData } from '@philips-emr/tws-utils';

import { Loader, NotFound } from '../..';
import { ReactComponent as CheckIcon } from '../../../resources/assets/components/status/check.svg';

import './selectable-iterator.scss';

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
];

interface SelectableIteratorEventArgs {
  element: ReactElement;
  name?: string;
  selected: boolean;
  value: any;
}

export class SelectableIteratorEvent<T> extends SelectEvent<T> {
  element: ReactElement;
  name?: string;

  constructor({ element, name, selected, value }: SelectableIteratorEventArgs) {
    super({ selected, value });

    this.element = element;
    this.name = name;
  }
}

export interface SelectableIteratorProps {
  children: ReactElement | ReactElement[];
  data: Record<string, Value>[];
  done: boolean;
  emptyResultExpression?: string;
  loading: boolean;
  multiselect: boolean;
  name?: string;
  onSelect: (event: SelectEvent<Record<string, Value>>) => void;
  primaryKey: string;
  selected: Record<string, Value>[];
  separator: boolean;
  showNotFound: boolean;
  uuid?: string;
}

@AwesomeComponent({ type: ComponentType.LIST, api: SelectableIteratorAPI })
class SelectableIterator extends Component<SelectableIteratorProps> {
  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultExpression: expression,
    loading: bool,
    multiselect: bool,
    name: string,
    onSelect: eventListener,
    primaryKey: string,
    selected: arrayOf(object),
    separator: bool,
    showNotFound: bool.notEnumerable(),
    uuid: string,
  };

  static defaultProps = {
    multiselect: false,
    primaryKey: 'id',
    selected: [],
    separator: true,
    showNotFound: true,
  };

  onSelectItem(item: Record<string, Value>): void {
    const { children, name, onSelect } = this.props;

    const childrenArray = Children.toArray<ReactElement>(children)[0];

    const child = cloneElement(childrenArray, childrenArray.props);

    if (isFunction(onSelect)) {
      onSelect(new SelectableIteratorEvent<Record<string, Value>>({
        element: child,
        name,
        selected: this.isSelected(item),
        value: item,
      }));
    }
  }

  isSelected(item: Record<string, Value>): boolean {
    const { name, primaryKey, selected = [] } = this.props;

    let selectedItems = selected;

    if (name) {
      selectedItems = selected.filter((item) => item && item.__name === name);
    }

    return selectedItems
      .map((selectedItem) => selectedItem && selectedItem[primaryKey])
      .includes(item[primaryKey]);
  }

  getClassName = (): string => {
    const { separator } = this.props;

    return classNames({
      'c-selectable-iterator': true,
      'c-selectable-iterator--with-separator': separator,
    });
  };

  renderItem(item: Record<string, Value>): ReactElement {
    const { children, primaryKey } = this.props;

    const className = classNames({
      'c-selectable-iterator__item': true,
      'is-selected': this.isSelected(item),
    });

    const inheritedProps = {
      className: 'c-selectable-iterator__component',
    };

    const key = String(item[primaryKey]);

    return (
      <div key={key} className={className} onClick={() => this.onSelectItem(item)}>
        {propagateData(children, item, inheritedProps)}
        {this.isSelected(item) && (
          <div className="c-selectable-iterator__icon">
            <CheckIcon />
          </div>
        )}
      </div>
    );
  }

  renderLoading(): ReactElement | null {
    const { loading } = this.props;

    if (!loading) {
      return null;
    }

    const size = isMobile() ? 32 : 64;

    return (
      <Loader className="c-selectable-iterator__loader u-hide@empty" centered size={size} />
    );
  }

  renderNotFound(): ReactElement | null {
    const { data, done, emptyResultExpression, showNotFound } = this.props;

    if (showNotFound && done && data.length === 0) {
      return <NotFound className="c-selectable-iterator__not-found" showImage={false} titleExpression={emptyResultExpression} />;
    }

    return null;
  }

  render(): ReactElement | null {
    const { children, data, uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    if (!Container || !uuid) {
      return null;
    }

    const content = (Array.isArray(data)) ? data.map((item) => this.renderItem(item)) : children;

    return (
      <Container acceptedTypes={acceptedTypes} className={this.getClassName()} uuid={uuid}>
        {content}
        {this.renderNotFound()}
        {this.renderLoading()}
      </Container>
    );
  }
}

export default SelectableIterator;
